import {
  CarDamagePictogram,
  ContractEndPictogram,
  CarPictogram,
} from '@velocity/icons/pictogram'
import { LinkInline } from '@velocity/ui'
import * as React from 'react'

import { useContent, useQuery } from '@ngb-frontend/shared/context'
import { ErrorVariants, UserType } from '@ngb-frontend/shared/types'
import {
  formatPhoneNumber,
  systemCodeToReferrer,
} from '@ngb-frontend/shared/utils'

import { DriverNotificationView } from '../DriverNotificationView/DriverNotificationView'
import { NotificationView } from '../NotificationView/NotificationView'

import type { NotificationViewProps } from '../NotificationView/NotificationView'

const DriverVariants: Partial<Record<ErrorVariants, string>> = {
  [ErrorVariants.SystemCode]: 'systemCode',
  [ErrorVariants.Vehicle]: 'vehicle',
  [ErrorVariants.ContractInactive]: 'contractInactiveDriver',
  [ErrorVariants.Country]: 'country',
  [ErrorVariants.UnsupportedCountry]: 'unsupportedCountry',
}

const ErrorIcons = {
  [ErrorVariants.Contract]: ContractEndPictogram,
  [ErrorVariants.ContractMalformed]: ContractEndPictogram,
  [ErrorVariants.ContractInactive]: CarPictogram,
  [ErrorVariants.Generic]: CarDamagePictogram,
}

export interface NotificationViewErrorProps
  extends Pick<NotificationViewProps, 'onSubmit' | 'Icon'> {
  type: UserType
  variant?: ErrorVariants
  /**
   * Optional retry handler. When provided, "Retry" becomes the primary action
   * and the referer link ("Back to …") is shown as a secondary outlined button.
   */
  onRetry?: () => void
}

export const NotificationViewError: React.FC<NotificationViewErrorProps> = ({
  type,
  onSubmit,
  onRetry,
  variant = ErrorVariants.Generic,
  // @ts-ignore
  Icon = ErrorIcons[variant] || ErrorIcons[ErrorVariants.Generic],
}) => {
  const c = useContent()
  const query = useQuery()
  const isDriver = type === UserType.Driver
  // Drivers get the flow's own look; every other persona keeps the card
  const View = isDriver ? DriverNotificationView : NotificationView

  // Missing initialization parameters or there is a user/service mismatch
  if (type === UserType.Unknown || variant === ErrorVariants.Service) {
    return (
      <View Icon={Icon} headline={c(`notifications.error.titles.${variant}`)} />
    )
  }

  const errorVariant = isDriver
    ? DriverVariants[variant] || ErrorVariants.Generic
    : variant

  const backText = c('notifications.error.submit', {
    variables: {
      referer: systemCodeToReferrer(query?.systemCode, query?.theme),
    },
  })

  return (
    <View
      Icon={Icon}
      headline={c(`notifications.error.titles.${errorVariant}`)}
      // With a retry handler: Retry is primary, "Back to …" is the secondary
      // outlined button. Without it: the referer link stays the single action.
      onSubmit={onRetry || onSubmit}
      submitButtonText={onRetry ? c('notifications.error.retry') : backText}
      submitButtonVariant={onRetry ? 'primary-filled' : 'secondary-outlined'}
      onCancel={onRetry ? onSubmit : undefined}
      cancelButtonText={backText}
      cancelButtonVariant="secondary-outlined"
    >
      {isDriver && !DriverVariants[variant] ? (
        <>
          {`${c('notifications.error.contactUs')} `}
          <LinkInline>
            {formatPhoneNumber(c('steps.shared.phoneNumber'))}
          </LinkInline>
        </>
      ) : (
        c(`notifications.error.content.${errorVariant}`, {
          fallback: 'notifications.error.contactHelpdesk',
        })
      )}
    </View>
  )
}
